import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import StateObserver, { user } from 'src/StateObserver';
import { Avatar } from 'src/game/components/shared/Avatar';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import buttons from 'src/lib/ui/config/buttons';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { MAX_MEMBERS } from 'src/replicant/team/team.ruleset';
import { TeamExtendedProfile } from 'src/replicant/team/team.schema';
import { getCurrentTeamProfile } from '../team.sequence';
import { assertNever } from 'src/replicant/utils';
import { track } from 'src/lib/analytics/utils';

export class TeamEntry extends View {
  private id?: string;
  private profile!: TeamExtendedProfile;
  private readonly bg: ImageScaleView;
  private readonly teamData: View;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly joinButton: ButtonScaleViewWithText;
  private readonly members: LangBitmapFontTextView;
  private readonly message: LangBitmapFontTextView;

  constructor(opts: ViewOpts) {
    super({ width: 700, height: 110, ...opts });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height + 7,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    this.teamData = new View({
      superview: this,
      width: this.style.width,
      height: this.style.height,
    });

    this.teamData.on('InputSelect', async () => {
      if (!this.id) return;
      sounds.sfx.play('ok');
      await this.openInfo();
    });

    this.name = new LangBitmapFontTextView({
      superview: this.teamData,
      x: 110,
      y: 20,
      align: 'left',
      verticalAlign: 'center',
      size: 34,
      font: 'Body',
      width: 150,
      height: 40,
      color: '#6d293c',
    });

    this.members = new LangBitmapFontTextView({
      superview: this.teamData,
      x: 110,
      y: 55,
      align: 'left',
      verticalAlign: 'center',
      size: 26,
      font: 'Body',
      width: 270,
      height: 40,
      color: '#b35248',
    });

    this.avatar = new Avatar({
      superview: this.teamData,
      width: 80,
      height: 80,
      x: 60,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    this.joinButton = new ButtonScaleViewWithText({
      ...buttons.secondary,
      superview: this.teamData,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: 165,
      height: 75,
      x: 590,
      y: this.style.height * 0.5,
      localeText: () => i18n('match2.team.join'),
      onClick: async () => {},
      centerOnOrigin: true,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.JOIN,
      },
    });

    this.message = new LangBitmapFontTextView({
      superview: this,
      x: 0,
      y: 0,
      align: 'center',
      verticalAlign: 'center',
      size: 34,
      font: 'Body',
      width: this.style.width,
      height: this.style.height,
      color: '#6d293c',
    });
  }

  public setup(team: TeamExtendedProfile | { message: string }) {
    if ('message' in team) {
      delete this.id;
      this.teamData.hide();
      this.message.show();
      this.message.localeText = () => team.message;
      return;
    }

    this.profile = team;

    this.id = team.id;
    this.teamData.show();
    this.message.hide();
    const shouldDisableButton = !!team.member;
    this.joinButton.updateOpts({ visible: !shouldDisableButton });

    this.name.localeText = () => team.name;
    this.avatar.updateImage(team.icon);
    this.members.localeText = () =>
      i18n('match2.team.members', {
        count: team.memberCount,
        max: MAX_MEMBERS,
      });

    this.bg.updateOpts({
      offsetY: 0,
      image: 'assets/ui/match2/common/list_entry.png',
    });

    if (team.member) {
      this.bg.updateOpts({
        image: 'assets/ui/match2/team/frames/panel_list_orange.png',
      });
    } else {
      this.bg.updateOpts({
        opacity: 1,
      });
    }
  }

  private async openInfo() {
    const status = await openPopupPromise('popupTeamInfo', {
      team: this.profile,
    });

    if (status !== 'join') return;
    if (!this.id) return;

    if (user().team.currentTeamId) {
      const current = await getCurrentTeamProfile()?.get();
      const confirm = await openPopupPromise('popupSwitchTeam', {
        current: current!,
        next: this.profile,
      });

      if (!confirm) return;
    }

    const result = await withLoading(
      () => StateObserver.invoke.asyncJoinTeam({ teamId: this.id! }),
      {
        feature: Feature.TEAM._,
        subFeature: Feature.TEAM.JOIN_TEAM,
      },
    );

    // Something went wrong, show error message.
    if ('error' in result && result.error) {
      switch (result.error) {
        case 'teamFull':
        case 'levelTooLow':
        case 'tryAgain':
          await openPopupPromise('popupError', {
            message: i18n(`match2.team.errors.${result.error}`),
          });
          break;
        default:
          assertNever(result);
      }
      return;
    }

    track('TeamJoin', {
      id: result.id,
      memberCount: result.memberCount,
      ownerPlayerId: result.ownerPlayerId,
      previousScore: result.previousScore,
    });

    this.emit('Close');

    void openPopupPromise('popupTeam', {});
  }
}
